/**
 * Generic per-track-id memoisation for the two "Full tracks" render modes
 * (standard and colour-coded) - both rebuild their entire GeoJSON source
 * from scratch on every refresh (pan, filter change, hour-range slider
 * move, ...), which redoes the same per-point geometry/colour work for
 * every track every single time, even when that track's underlying data
 * hasn't changed at all since the last rebuild. Single shared
 * implementation so tracksFullStandard.js and tracksFullColour.js stay in
 * sync rather than each growing its own cache.
 *
 * Cache validity is a `===` reference check against whatever object
 * (fullTrackTraces[trackId], or the coarse stub feature while the real
 * trace is still loading) drove the last computation for that track -
 * redux/props objects are only ever replaced wholesale, never mutated in
 * place, so a reference change reliably means "this track's data actually
 * changed" and a same reference reliably means "safe to reuse".
 */

/**
 * Returns the cached result for `trackId` if `sourceRef` still matches what
 * produced it, otherwise runs `computeFn`, caches, and returns the result.
 * @param {Map<string, {sourceRef: any, result: any}>} cacheMap
 * @param {string} trackId
 * @param {object} sourceRef - the object this computation depends on.
 * @param {() => any} computeFn
 * @returns {any}
 */
export function computeWithTrackCache(cacheMap, trackId, sourceRef, computeFn) {
  const cached = cacheMap.get(trackId);
  if (cached && cached.sourceRef === sourceRef) {
    return cached.result;
  }
  const result = computeFn();
  cacheMap.set(trackId, { sourceRef, result });
  return result;
}

/**
 * Drops cache entries for track ids no longer in `activeTrackIds`, so
 * memory doesn't accumulate for tracks panned away from long ago.
 * @param {Map<string, unknown>} cacheMap
 * @param {Set<string>} activeTrackIds
 */
export function pruneTrackCache(cacheMap, activeTrackIds) {
  cacheMap.forEach((_, trackId) => {
    if (!activeTrackIds.has(trackId)) cacheMap.delete(trackId);
  });
}
